import Snackbar from '@mui/material/Snackbar';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import Chip from '@mui/material/Chip';
import {
  GridRowModesModel,
  GridRowModes,
  GridRowParams,
  GridSelectionModel,
  GridRowId,
  GridFilterModel,
  GridSortModel,
  getGridStringOperators,
  GridActionsCellItem,
  GridColumns,
} from '@mui/x-data-grid-pro';
import Alert, { AlertProps } from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { CustomDataTable } from 'components/DatatableComponent';
import { MainLayoutComponent } from 'components/LayoutComponent/SidebarLayout/MainLayoutComponent';
import { PaginationAndSortingParams } from 'types/api/Common/PaginationTypes';
import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import { getErrorMessage } from 'utils/Error';
import { DASHBOARD_VIEW_MODE } from 'constant/DashboardTypeConstant';
import { useGetCubeLayerSetList, useUpsertCubeLayerSet } from 'services/v1/SystemTenant/AROCube/CubeLayerSetService';
import { CubeLayerSetRowItem, RequestCreateCubeLayerSet } from 'types/api/SystemTenant/AROCube/CubeLayerSetTypes';
import { ModalSelectLayer, ModalSelectLayerParamsProps } from './components/ModalSelectLayer';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';

const panelStyle = {
  transform: 'translate3d(704px, 140.667px, 0px) !important',

  '& .MuiDataGrid-panelFooter': {
    display: 'none',
  },
};

const chipSx = {
  height: '20px',
  backgroundColor: '#C8D7EC',
};

export default function CubeLayerSetListPage() {
  const { dashboardViewMode } = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();
  const [rowData, setRowData] = useState<CubeLayerSetRowItem[]>([]);

  const [modalSelectLayer, setModalSelectLayer] = useState<ModalSelectLayerParamsProps>({
    visible: false,
    layerSetItem: null,
  });

  const [selectedDataID, setSelectedDataID] = useState<GridRowId[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [filter, setFilter] = useState<PaginationAndSortingParams>({
    page: 0,
    size: 20,
    filterValue: '',
    sortType: 'asc',
    filterOperator: 'contains',
  });

  const [filterButtonElement, setFilterButtonElement] = useState<HTMLButtonElement | null>(null);
  const [editedData, setEditedData] = useState<CubeLayerSetRowItem[]>([]);
  const [snackbar, setSnackbar] = useState<Pick<AlertProps, 'children' | 'severity'> | null>(null);

  const { data: cubeLayerSetList, isLoading, refetch: refreshCubeSetLayerData } = useGetCubeLayerSetList();
  const { mutateAsync: upsertCubeLayerSetAsync } = useUpsertCubeLayerSet();

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const isAnyEdit = useMemo(() => {
    return rowModesModel && Object.values(rowModesModel).some((item) => item.mode === GridRowModes.Edit);
  }, [rowModesModel]);

  const isBuilderViewOnly = dashboardViewMode === DASHBOARD_VIEW_MODE.VIEW_ONLY;

  const columns = useMemo<GridColumns>(() => {
    const cols: GridColumns = [
      {
        field: 'code',
        headerName: 'Code',
        width: 80,
        editable: false,
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
      {
        field: 'name',
        headerName: 'Layer Set Name',
        width: 200,
        editable: true,
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },

      {
        field: 'description',
        headerName: 'Description',
        width: 200,
        editable: true,
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
      {
        field: 'layers',
        headerName: 'Layer Set',
        width: 400,
        editable: false,
        renderCell: (params) => {
          const row = params.row as CubeLayerSetRowItem;
          if (row.isNew) return false;
          // only show 3 layers, and show the rest as a number
          const layers = row.layers.slice(0, 3);
          const rest = row.layers.length - 3;

          return (
            <Stack direction='row' spacing={1} alignItems='center'>
              {layers.map((layer) => (
                <Chip key={layer.value} label={layer.label} sx={chipSx} />
              ))}
              {rest > 0 && <Chip label={`+${rest}`} sx={chipSx} />}
              <Chip
                label='+'
                sx={chipSx}
                onClick={() => {
                  setModalSelectLayer({ visible: true, layerSetItem: row });
                }}
              />
            </Stack>
          );
        },
      },
    ];

    if (isBuilderViewOnly) return cols;

    return [
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Edit',
        width: 30,
        cellClassName: 'actions',

        getActions: ({ id }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

          if (isInEditMode) {
            // @ts-ignore
            return [<GridActionsCellItem icon={<SaveIcon />} label='Save' onClick={handleSaveClick(id)} />];
          }
          return [
            // @ts-ignore
            <GridActionsCellItem disabled={isAnyEdit} icon={<EditIcon />} label='Edit' onClick={handleEditClick(id)} />,
          ];
        },
      },
      ...cols,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowModesModel, isAnyEdit, isBuilderViewOnly]);

  const DataMutationSuccessHandlerOption = {
    onSuccess: () => {
      refreshCubeSetLayerData();
    },
    onError: (error: AxiosDefaultErrorEntity) => {
      refreshCubeSetLayerData();
      setSnackbar({ children: getErrorMessage(error), severity: 'error' });
    },
  };

  useEffect(() => {
    if (cubeLayerSetList?.data) {
      setRowData(cubeLayerSetList?.data);
      setRowModesModel(
        cubeLayerSetList?.data?.reduce((acc, item) => {
          return { ...acc, [item.id]: GridRowModes.View };
        }, {})
      );
    }
  }, [cubeLayerSetList]);

  const handleChangePage = (newPage: number) => {
    setFilter({ ...filter, page: newPage });
  };
  const handleChangePageSize = (newPageSize: number) => {
    setFilter({ ...filter, size: newPageSize });
  };

  const handleOnFilter = (params: GridFilterModel) => {
    let filterObj = {};
    if (params.items.length) {
      filterObj = {
        filterValue: params.items[0].value,
        filterOperator: params.items[0].operatorValue,
      };
    } else {
      filterObj = {
        filterValue: '',
        filterOperator: 'contains',
      };
    }
    setFilter({
      ...filter,
      ...filterObj,
    });
  };

  const handleOnSortModelChange = (model: GridSortModel) => {
    if (model.length === 0) {
      setFilter({ ...filter, sortType: 'asc' });
      return;
    }
    setFilter({ ...filter, sortType: model[0].sort });
  };

  // Process row Update will be triggered when
  // user modify or insert new row the table
  const handleProcessRowUpdate = (data: CubeLayerSetRowItem, oldData: CubeLayerSetRowItem) => {
    console.log({
      data,
    });
    const isEmpty = !data.name && !data.description;
    const isAnyFieldEmpty = !data.name || !data.description;
    if (isAnyFieldEmpty) return;
    if (isEmpty) handleCancelInsert(data.id);
    if (data.isNew) {
      const insertPayload: RequestCreateCubeLayerSet = {
        description: data.description,
        name: data.name,
        layers: [],
      };
      // if the row is new and not empy, then insert the row to database
      upsertCubeLayerSetAsync(insertPayload, DataMutationSuccessHandlerOption);
      return { ...data, isNew: false };
    }
    // if the row is not new, it means that the row n
    if (!data.isNew) {
      // if data already exist in state editedData, update it
      // if not, add it to editedData
      const index = editedData.findIndex((item) => item.id === data.id);
      if (index === -1) {
        setEditedData([...editedData, data]);
      } else {
        const newEditedData = [...editedData];
        newEditedData[index] = data;
        setEditedData(newEditedData);
      }
      const updatePayload: RequestCreateCubeLayerSet = {
        id: data.id,
        name: data.name,
        description: data.description,
        layers: data.layers.map((layer) => layer.value),
      };
      upsertCubeLayerSetAsync(updatePayload, DataMutationSuccessHandlerOption);
      return data;
    }
  };

  const handleCancelInsert = (id: string | number) => {
    const newRows = rowData.filter((item) => item.id !== id);
    setRowData(newRows);
    setRowModesModel((prev) => {
      const newModel = { ...prev };
      delete newModel[id];
      return newModel;
    });
  };

  const handleEditRowModes = (params: GridRowParams) => {
    if (isBuilderViewOnly || isAnyEdit) return;
    setRowModesModel({ ...rowModesModel, [params.id]: { mode: GridRowModes.Edit } });
  };

  const handleCloseSnackbar = () => setSnackbar(null);

  return (
    <MainLayoutComponent
      pageTitle='Cube Layer Set'
      breadcrumbs={[t('SIDEBAR_MENU.DASHBOARD'), t('SIDEBAR_MENU.DATA_BUILDER')]}
    >
      <CustomDataTable
        initialState={{
          pagination: {
            page: 0,
          },
          sorting: {
            sortModel: [
              {
                field: 'code',
                sort: 'asc',
              },
            ],
          },
        }}
        loading={isLoading}
        checkboxSelection={!isBuilderViewOnly}
        rows={rowData}
        columns={columns}
        rowCount={cubeLayerSetList?.data?.length || 0}
        page={filter.page || 0}
        pageSize={filter.size}
        getRowId={(row) => row.id}
        rowModesModel={rowModesModel}
        disableSelectionOnClick
        rowsPerPageOptions={[5, 10, 20]}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
        experimentalFeatures={{ newEditingApi: true }}
        onFilterModelChange={handleOnFilter}
        onSortModelChange={handleOnSortModelChange}
        onPageChange={handleChangePage}
        onPageSizeChange={handleChangePageSize}
        onSelectionModelChange={(selectedId: GridSelectionModel) => {
          setSelectedDataID(selectedId);
        }}
        processRowUpdate={handleProcessRowUpdate}
        onRowDoubleClick={handleEditRowModes}
        paginationMode='client'
        sortingMode='client'
        filterMode='client'
        componentsProps={{
          panel: {
            sx: panelStyle,
            anchorEl: filterButtonElement,
          },
          toolbar: {
            setRows: setRowData,
            setRowModesModel,
            setFilterButtonElement: setFilterButtonElement,
            disabled: isLoading || isBuilderViewOnly,
            isEditing: rowData.some((item) => item.isNew),
            disabledDeleteButton: selectedDataID.length === 0,
            config: {
              deleteButtonText: 'Delete',
              fieldFocusOnInsert: 'name',
              insertButtonText: 'Add New Layer Set',
              insertInitialObject: {
                name: '',
                layers: [],
                description: '',
                isNew: true,
              },
            },
          },
        }}
      />

      {!!snackbar && (
        <Snackbar
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={handleCloseSnackbar}
          autoHideDuration={3000}
        >
          <Alert {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}

      <ModalSelectLayer
        visible={modalSelectLayer.visible}
        layerSetItem={modalSelectLayer.layerSetItem}
        onClose={() => setModalSelectLayer({ ...modalSelectLayer, visible: false })}
      />
    </MainLayoutComponent>
  );
}
